import { fromNano } from '@ton/core/dist/utils/convert';
import {
  getTonBalance,
  mockTonChainId,
  mockTonTestnetChainId,
  sendTransaction as sendTonTx,
} from '../../../config/ton';
import {
  initOptions,
  TonTransferBodyLegacy,
  TonTxBodyType,
  TonTxParams,
  TonTxRequestStandard,
  TonTxRequest,
  TomoProviderEventName,
} from '../../types/types';
import { parsingTonTxPayload } from '../../utils/ton';
import BasicProvider from '../BasicProvider';
import ProviderUtils from '../ProviderUtils';
import { checkIsJettonWallet } from '../../utils/ton/jetton';
import { isValidHex } from '../../utils/isValidHex';
import { base64ToHex } from '../../utils/base64toHex';

export class TonProvider extends BasicProvider {
  utils: ProviderUtils;
  account: {
    address: string;
    publicKey?: string;
    tonProof?: any;
  };
  chainId: number;
  isConnected: boolean;

  constructor(options: initOptions) {
    super(options);
    this.chainId = mockTonChainId;
    this.isConnected = false;
    this._initialize();
  }

  private _initialize() {
    const account = this.utils.storage.get(`accounts`)?.ton;
    if (account) {
      this.account = account;
      this.isConnected = true;
      this.chainId = account.chainId || mockTonChainId;
    }
  }

  // @ts-ignore this is still not elegant
  async connect(params?: {
    tonProof?: string;
    domain?: string;
    chainId?: number;
    email?: string;
    workChain?: number;
    network?: string;
  }) {
    if (params?.network === 'testnet') {
      this.chainId = mockTonTestnetChainId;
      params.chainId = mockTonTestnetChainId;
    }
    if (params?.tonProof) {
      params.domain = params.domain || window.location.hostname;
      params.workChain = params.workChain ?? 0;
      params.chainId = mockTonChainId;
    }

    const res: any = (await super.connect('ton_connectWallet', params)) || {
      address: '',
    };

    if (res.address) {
      this.account = res;
      this.isConnected = true;
    }

    return res;
  }

  get connected() {
    return this.isConnected;
  }

  async restoreConnection() {
    return this.getAddress();
  }

  getAddress() {
    return this.account?.address || '';
  }

  async getBalance(address?: string) {
    if (!address && !this.account?.address)
      return { balance: '0', formatted: '0' };
    const res = await getTonBalance({ tonAddress: address || this.account.address, chainId: this.chainId });
    return res;
  }

  private async signTransaction(txs: TonTxRequest[]) {
    const newTxs = txs.map(v => ({
      chainId: this.chainId,
      publicKey: this.account.publicKey,
      ...v,
    }));
    const res = await this.utils.request({
      method: 'ton_signTransaction',
      params: [...newTxs],
    });
    return res;
  }

  private async signTx(txs: TonTxRequest[]) {
    console.log('this.chainId', this.chainId);
    const newTxs = txs.map(v => ({
      chainId: this.chainId,
      publicKey: this.account.publicKey,
      ...v,
    }));
    const res = await this.utils.request({
      method: 'ton_signTx',
      params: [...newTxs],
    });
    return res;
  }

  async sendTransaction(params: TonTxParams) {
    return this.sendTx(params);
  }

  async sendTx(params: TonTxParams) {
    if (!params.from) params.from = this.getAddress();
    const { messages, from } = params;
    if (messages.length === 0) {
      throw new Error('No messages');
    }

    const txParam = await this.parsePayloadAsStandard(params);
    this.checkValue(txParam);
    console.log({ txParam });
    const signRes = await this.signTx([txParam]);
    const res = await sendTonTx(signRes.result, '', this.chainId);
    return res;
  }

  private checkValue(param: Partial<TonTxRequestStandard>) {
    try {
      fromNano(param.body.messages[0].amount);
    } catch (e) {
      throw new Error('amount must in the format of nanoTon');
    }
  }

  /**
   * @deprecated no longer maintained in the future
   */
  private parsePayloadAsJSON(
    body: TonTransferBodyLegacy,
    payload: string
  ): TonTransferBodyLegacy {
    const payloadObj = JSON.parse(payload);
    const { contractAddr, precision, forwardAmount, memo } = payloadObj;
    return {
      ...body,
      contractAddress: contractAddr,
      precision,
      forwardAmount,
      memo,
      type: TonTxBodyType.JETTON_PAYLOAD_JSON_LEGACY,
    };
  }

  private async parsePayloadAsStandard(
    tonTx: TonTxParams
  ): Promise<Partial<TonTxRequestStandard>> {
    const {
      messages: [{ payload, address: toAddress }],
    } = tonTx;

    const result: Partial<TonTxRequestStandard> = {
      type: TonTxBodyType.STANDARD,
      body: tonTx,
    };

    if (!payload) return result;

    let payloadHex = payload;
    if (!isValidHex(payloadHex)) {
      // auto convert base64 to hex
      payloadHex = base64ToHex(payloadHex);
      result.body.messages[0].payload = payloadHex;
    }

    const jettonMinterAddress = await checkIsJettonWallet(toAddress);
    // check is jetton and try parse as jetton
    if (!jettonMinterAddress) return result;
    try {
      const { amount, destination } = parsingTonTxPayload(payloadHex);

      result.jettonInfo = {
        recipientAddress: destination.toString(),
        amount: amount.toString(),
        jettonMinterAddress,
      };
    } catch (e) {
      console.log('parse payload failed, pass');
      // throw 'call contract other than jetton is not supported yet';
    }

    return result;
  }

  async disconnect() {
    this.account = null;
    super.disconnect('ton_disconnect');
    this.emit(TomoProviderEventName.TOMO_TON_PROVIDER_DISCONNECTED, '');
  }
}
